import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import {SchemaCollaborator} from 'auth/PermissionConstants';
import {useSchemaCollaboratorsSection} from 'collaborator-components/useSchemaCollaboratorsSection';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SchemaWithOrgPermissionsSettings} from 'schemas/SchemaWithOrgPermissionsSettings';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  StudySchemaPermissionSettingsQuery,
  StudySchemaPermissionSettingsQueryVariables,
} from './__generated/StudySchemaPermissionSettingsQuery';
import {updateCollaborators} from './StudySchemaDAO';

type StudySchema = ExtractByTypename<StudySchemaPermissionSettingsQuery['studySchema'], 'StudySchemaNode'>;

export const STUDY_SCHEMA_PERMISSION_SETTINGS_QUERY = gql`
  query StudySchemaPermissionSettingsQuery($studySchemaId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    studySchema(id: $studySchemaId) {
      ... on PoliceableInterface {
        id
        permissions {
          ...SchemaPermissionsGraphQL
        }
        ...CollaboratorInfoGraphQL
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('CollaboratorInfo').fragment}
  ${getFragmentWrapper('SchemaPermissions').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

async function updateStudySchemaCollaborators(
  schemaId: string,
  data: {
    collaborators: Array<SchemaCollaborator>;
  }
): Promise<void> {
  const response = await updateCollaborators(schemaId, data);
  writeFragmentsToCache(getFragmentWrapper('CollaboratorInfo'), [response.schema]);
}

export interface QueryingProps {
  studySchemaId: string;
}
function QueryingStudySchemaPermissionSettings({studySchemaId}: QueryingProps): JSX.Element | null {
  const result = useBenchlingQuery<
    StudySchemaPermissionSettingsQuery,
    StudySchemaPermissionSettingsQueryVariables
  >(STUDY_SCHEMA_PERMISSION_SETTINGS_QUERY, {
    variables: {studySchemaId},
  });
  if (result.type === QueryResultType.ERROR) {
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    assert(!isInaccessible(result.data.studySchema), 'Expected study schema to be accessible.');
    return (
      <ExistingStudySchemaPermissionSettings
        studySchema={result.data.studySchema}
        refetch={async () => {
          await result.refetch();
        }}
      />
    );
  }
}

export function ExistingStudySchemaPermissionSettings({
  studySchema,
  refetch,
}: {
  studySchema: StudySchema;
  refetch: () => Promise<void>;
}): JSX.Element {
  const [{collaborators, minimalCollaborators}, handleUpdate] = useSchemaCollaboratorsSection(
    studySchema.collaboratorsJson
  );
  return (
    <SchemaWithOrgPermissionsSettings
      id={studySchema.id}
      isReadOnly={!studySchema.permissions.definitionEditable}
      collaborators={collaborators}
      onUpdate={handleUpdate}
      onSubmit={async () => {
        await updateStudySchemaCollaborators(studySchema.id, {
          collaborators: minimalCollaborators,
        });
        refetch();
      }}
    />
  );
}

export default QueryingStudySchemaPermissionSettings;
